import * as React from 'react';

import { useQuery } from '@apollo/client';

import InviteRostersByProgramIdGql from 'bundles/enterprise-admin-learners/queries/InviteRostersByProgramId.graphql';
import type {
  InviteRostersByProgramIdQuery,
  InviteRostersByProgramIdQueryVariables,
} from 'bundles/enterprise-admin-learners/queries/__generated__/InviteRostersByProgramId';

/**
 * Shared data fetch behind `useContractEligibleGroupIds` (picker eligibility) and
 * `useRosterIdForContract` (submission-time roster resolution) — both need the same
 * program-id -> roster-under-contract lookup, just consumed differently.
 */
export const useInviteRostersByProgramIds = (programIds: string[], contractId: string | undefined) => {
  const { data, loading, error } = useQuery<InviteRostersByProgramIdQuery, InviteRostersByProgramIdQueryVariables>(
    InviteRostersByProgramIdGql,
    {
      variables: { programIds },
      skip: !contractId || programIds.length === 0,
      context: { clientName: 'gatewayGql' },
    }
  );

  const rosters = data?.EnterpriseRosters?.queryRostersByProgramIdsOrContractIdsOrBothAndIncludeDeleted?.elements;

  // Only the rosters matching `contractId`, keyed by program id — `undefined` means "not
  // resolved yet" (or nothing to filter by), distinct from an empty/no-match result.
  const rosterIdByProgramId = React.useMemo(() => {
    if (!contractId || !rosters) return undefined;
    const map = new Map<string, string>();
    rosters.forEach((roster) => {
      if (roster.contract?.id === contractId) map.set(roster.program.id, roster.id);
    });
    return map;
  }, [rosters, contractId]);

  return { rosterIdByProgramId, loading, error };
};

export default useInviteRostersByProgramIds;
